import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown, Trash2, Plus, FileText, Lightbulb, Target,
} from '../icons';
import type { StageDetail, PathwayAsset, PathwayTask, AssetType, TaskType } from '../../types/pathway';

interface StageEditorProps {
  stage: StageDetail;
  index: number;
  totalStages: number;
  onUpdateField: (field: string, value: string) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onAddVideo: () => void;
  onUploadFile: (type: AssetType) => void;
  onDeleteAsset: (assetId: string, storagePath?: string) => void;
  onAddTask: (type: TaskType) => void;
  onUpdateTask: (taskId: string, prompt: string) => void;
  onDeleteTask: (taskId: string) => void;
}

function assetIcon(type: AssetType) {
  switch (type) {
    case 'video_embed': return '▶';
    case 'pdf': return '📄';
    case 'audio': return '🎵';
    case 'worksheet': return '📋';
    default: return '📎';
  }
}

export function StageEditor({
  stage,
  index,
  totalStages,
  onUpdateField,
  onDelete,
  onMoveUp,
  onMoveDown,
  onAddVideo,
  onUploadFile,
  onDeleteAsset,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
}: StageEditorProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="freud-card-elevated overflow-hidden"
    >
      <div
        className="flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-cream-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="w-8 h-8 bg-sage-100 rounded-lg flex items-center justify-center text-sm font-semibold text-sage-700 flex-shrink-0">
          {index + 1}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-chocolate-900 truncate">
            {stage.title || `Stage ${index + 1}`}
          </p>
          {stage.objective && (
            <p className="text-xs text-chocolate-400 truncate">{stage.objective}</p>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-xs text-chocolate-400 mr-2">
            {stage.assets.length} assets, {stage.tasks.length} tasks
          </span>
          <button
            onClick={e => { e.stopPropagation(); onMoveUp(); }}
            disabled={index === 0}
            className="p-1 hover:bg-cream-200 rounded disabled:opacity-30 text-xs text-chocolate-500"
            title="Move up"
          >
            ↑
          </button>
          <button
            onClick={e => { e.stopPropagation(); onMoveDown(); }}
            disabled={index === totalStages - 1}
            className="p-1 hover:bg-cream-200 rounded disabled:opacity-30 text-xs text-chocolate-500"
            title="Move down"
          >
            ↓
          </button>
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="p-1.5 hover:bg-warm-50 hover:text-warm-600 rounded-lg transition-colors text-chocolate-400"
            title="Delete stage"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="ml-1"
          >
            <ChevronDown className="w-4 h-4 text-chocolate-400" />
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-5 border-t border-cream-200 pt-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-chocolate-600 mb-1">Title</label>
                  <input
                    type="text"
                    value={stage.title}
                    onChange={e => onUpdateField('title', e.target.value)}
                    placeholder="Stage title"
                    className="w-full border border-cream-300 rounded-xl px-3.5 py-2 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-chocolate-600 mb-1">Objective</label>
                  <input
                    type="text"
                    value={stage.objective}
                    onChange={e => onUpdateField('objective', e.target.value)}
                    placeholder="What will the client achieve?"
                    className="w-full border border-cream-300 rounded-xl px-3.5 py-2 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-chocolate-600 mb-1">Description</label>
                <textarea
                  value={stage.description}
                  onChange={e => onUpdateField('description', e.target.value)}
                  placeholder="Detailed description or instructions for this stage..."
                  rows={3}
                  className="w-full border border-cream-300 rounded-xl px-3.5 py-2 text-sm text-chocolate-800 placeholder:text-chocolate-400 focus:outline-none focus:ring-2 focus:ring-sage-300 focus:border-sage-400 resize-none"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-chocolate-600">Assets</label>
                  <div className="flex gap-1.5">
                    <button
                      onClick={onAddVideo}
                      className="text-xs px-2.5 py-1 bg-cream-100 hover:bg-cream-200 text-chocolate-600 rounded-lg transition-colors"
                    >
                      + Video
                    </button>
                    <button
                      onClick={() => onUploadFile('pdf')}
                      className="text-xs px-2.5 py-1 bg-cream-100 hover:bg-cream-200 text-chocolate-600 rounded-lg transition-colors"
                    >
                      + PDF
                    </button>
                    <button
                      onClick={() => onUploadFile('worksheet')}
                      className="text-xs px-2.5 py-1 bg-cream-100 hover:bg-cream-200 text-chocolate-600 rounded-lg transition-colors"
                    >
                      + Worksheet
                    </button>
                    <button
                      onClick={() => onUploadFile('audio')}
                      className="text-xs px-2.5 py-1 bg-cream-100 hover:bg-cream-200 text-chocolate-600 rounded-lg transition-colors"
                    >
                      + Audio
                    </button>
                  </div>
                </div>
                {stage.assets.length === 0 ? (
                  <p className="text-xs text-chocolate-400 italic py-2">No assets added yet</p>
                ) : (
                  <div className="space-y-1.5">
                    {stage.assets.map(asset => (
                      <div key={asset.id} className="flex items-center gap-2 px-3 py-2 bg-cream-50 rounded-lg">
                        <span className="text-sm">{assetIcon(asset.type)}</span>
                        <span className="text-xs text-chocolate-700 flex-1 truncate">{asset.title}</span>
                        <span className="text-[10px] text-chocolate-400 uppercase">{asset.type.replace('_', ' ')}</span>
                        <button
                          onClick={() => onDeleteAsset(asset.id, asset.storagePath || undefined)}
                          className="p-1 hover:bg-warm-100 rounded text-chocolate-400 hover:text-warm-600 transition-colors"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-chocolate-600">Tasks</label>
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => onAddTask('reflection')}
                      className="text-xs px-2.5 py-1 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg transition-colors inline-flex items-center gap-1"
                    >
                      <Lightbulb className="w-3 h-3" />
                      Reflection
                    </button>
                    <button
                      onClick={() => onAddTask('action')}
                      className="text-xs px-2.5 py-1 bg-orange-40 hover:bg-orange-100 text-orange-700 rounded-lg transition-colors inline-flex items-center gap-1"
                    >
                      <Target className="w-3 h-3" />
                      Action Step
                    </button>
                  </div>
                </div>
                {stage.tasks.length === 0 ? (
                  <p className="text-xs text-chocolate-400 italic py-2">No tasks added yet</p>
                ) : (
                  <div className="space-y-2">
                    {stage.tasks.map(task => (
                      <div key={task.id} className="flex items-start gap-2 px-3 py-2 bg-cream-50 rounded-lg">
                        <span className={`text-[10px] uppercase font-medium px-1.5 py-0.5 rounded mt-0.5 ${
                          task.type === 'reflection'
                            ? 'bg-teal-100 text-teal-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {task.type}
                        </span>
                        <textarea
                          value={task.prompt}
                          onChange={e => onUpdateTask(task.id, e.target.value)}
                          className="flex-1 text-xs text-chocolate-700 bg-transparent resize-none border-0 focus:outline-none focus:ring-0 p-0 min-h-[20px]"
                          rows={1}
                          placeholder="Enter prompt..."
                        />
                        <button
                          onClick={() => onDeleteTask(task.id)}
                          className="p-1 hover:bg-warm-100 rounded text-chocolate-400 hover:text-warm-600 transition-colors flex-shrink-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
